import React, { useState, useContext, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  CloudUpload,
  Download,
  Description,
  Visibility,
  CheckCircle,
  Close,
  Business,
  Receipt,
  GetApp,
  FileDownloadOutlined
} from '@mui/icons-material';
import { FaFileExcel, FaFileInvoice } from 'react-icons/fa';
import { NotificationContext } from '../context/NotificationContext';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const SafaviehInvoices = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const [files, setFiles] = useState([]);
  const [companyType, setCompanyType] = useState('LLC');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);
  const fileIndexRef = useRef(0);
  const processedFilesRef = useRef([]);
  const totalInvoicesRef = useRef(0);
  const fileInputRef = useRef(null);

  const validateAndAddFiles = (selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    if (fileArray.length === 0) return;

    const excelFiles = fileArray.filter(file =>
      file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')
    );

    if (excelFiles.length !== fileArray.length) {
      setError('Only Excel files (.xlsx, .xls) are supported');
      return;
    }

    setFiles(prev => [...prev, ...excelFiles]);
    setError('');
  };

  const handleFileUpload = (event) => {
    validateAndAddFiles(event.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    validateAndAddFiles(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    setFiles(files => files.filter((_, i) => i !== index));
  };

  // Fetch status for current file processing
  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current;
    if (!currentProcessId) return;

    try {
      const statusData = await apiCall(API_ENDPOINTS.SAFAVIEH_INVOICES_STATUS(currentProcessId), {
        method: 'GET',
      });

      if (statusData) {
        const progressPct = statusData.progressPercentage ?? 0;
        const logs = statusData.logs ?? [];
        const currentStatus = statusData.status ?? '';

        // Update global activity log
        updateActivityProcess(currentProcessId, {
          progressPercentage: progressPct,
        });

        // Update logs in global context
        if (logs && logs.length > 0) {
          setActivityLogs(currentProcessId, logs);
        }

        // Check if processing is complete
        if (currentStatus === 'completed' || currentStatus === 'error') {
          // Clear interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          clearIntervalRef(currentProcessId);

          // Complete the activity process
          completeActivityProcess(currentProcessId, currentStatus === 'completed');

          if (currentStatus === 'completed') {
            // Auto-download the file
            if (!downloadTriggeredRef.current && statusData.downloadUrl) {
              downloadTriggeredRef.current = true;
              const fileName = `Expense_Allocation_${companyType}_${files[fileIndexRef.current]?.name.replace(/\.[^/.]+$/, '')}.pdf`;
              await downloadFile(statusData.downloadUrl, fileName);
            }

            // Track completed file
            processedFilesRef.current.push(files[fileIndexRef.current].name);
            totalInvoicesRef.current += statusData.invoiceCount || 0;

            // Check if there are more files to process
            if (fileIndexRef.current < files.length - 1) {
              // Process next file
              fileIndexRef.current++;
              setCurrentFileIndex(fileIndexRef.current);
              downloadTriggeredRef.current = false;
              await processNextFile();
            } else {
              // All files processed
              setProcessing(false);
              setSuccess(`Successfully processed ${processedFilesRef.current.length} file(s) and generated ${totalInvoicesRef.current} invoice(s)!`);
              setResults({
                processedCount: processedFilesRef.current.length,
                totalCount: files.length,
                invoiceCount: totalInvoicesRef.current,
                companyType
              });
            }
          } else if (currentStatus === 'error') {
            setError('Processing failed. Check the activity log for details.');
            // Try next file if any
            if (fileIndexRef.current < files.length - 1) {
              fileIndexRef.current++;
              setCurrentFileIndex(fileIndexRef.current);
              downloadTriggeredRef.current = false;
              await processNextFile();
            } else {
              setProcessing(false);
              if (processedFilesRef.current.length > 0) {
                setSuccess(`Processed ${processedFilesRef.current.length} of ${files.length} file(s). Some files had errors.`);
              }
              setResults({
                processedCount: processedFilesRef.current.length,
                totalCount: files.length,
                invoiceCount: totalInvoicesRef.current,
                companyType
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  // Process next file in queue
  const processNextFile = async () => {
    const file = files[fileIndexRef.current];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('companyType', companyType);

      const result = await apiCall(API_ENDPOINTS.SAFAVIEH_INVOICES_GENERATE, {
        method: 'POST',
        body: formData
      });

      if (result.processId) {
        processIdRef.current = result.processId;

        // Add to global activity log
        const entityName = companyType === 'LLC' ? 'LLC' : companyType === 'Broadway' ? 'Broadway' : 'Glen Cove';
        addActivityProcess(result.processId, `Safavieh Invoice (${entityName}): ${file.name}`, 'Safavieh Invoices');

        // Start polling for status
        intervalRef.current = setInterval(() => {
          fetchStatus();
        }, 1000);
        setIntervalRef(result.processId, intervalRef.current);
      } else {
        setError('Failed to start processing');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Error starting processing:', err);
      setError(`Failed to process ${file.name}: ${err.message}`);
      // Try next file if any
      if (fileIndexRef.current < files.length - 1) {
        fileIndexRef.current++;
        setCurrentFileIndex(fileIndexRef.current);
        await processNextFile();
      } else {
        setProcessing(false);
      }
    }
  };

  const processInvoices = async () => {
    if (files.length === 0) {
      setError('Please select Excel files before processing.');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');
    setResults(null);

    // Reset refs
    fileIndexRef.current = 0;
    setCurrentFileIndex(0);
    downloadTriggeredRef.current = false;
    processedFilesRef.current = [];
    totalInvoicesRef.current = 0;

    // Start processing first file
    await processNextFile();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const downloadFile = async (downloadUrl, fileName) => {
    try {
      const encodedUrl = encodeURIComponent(downloadUrl);
      const response = await apiCall(API_ENDPOINTS.SAFAVIEH_INVOICES_DOWNLOAD(encodedUrl));

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await apiCall(API_ENDPOINTS.SAFAVIEH_INVOICES_TEMPLATE);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'Expense_Allocation_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification('Template downloaded successfully', 'success');
    } catch (error) {
      console.error('Template download error:', error);
      setError('Template download failed');
    }
  };

  const resetForm = () => {
    setFiles([]);
    setError('');
    setSuccess('');
    setResults(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCompanyDescription = () => {
    switch (companyType) {
      case 'LLC':
        return 'Generate invoices for Safavieh International LLC entity';
      case 'Broadway':
        return 'Generate invoices for Safavieh Broadway entity';
      case 'Glen':
        return 'Generate invoices for Safavieh Glen Cove entity';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Expense Allocation Invoices" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          EXPENSE ALLOCATION INVOICES
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Invoice Generation
              </Typography>

              {/* Company Type Selection */}
              <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
                <CardContent>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business sx={{ color: TOOL_COLORS.PDF.primary }} />
                        Select Safavieh Entity
                      </Box>
                    </FormLabel>
                    <RadioGroup
                      value={companyType}
                      onChange={(e) => setCompanyType(e.target.value)}
                      row
                      sx={{ mt: 1 }}
                    >
                      <FormControlLabel
                        value="LLC"
                        control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business sx={{ color: TOOL_COLORS.PDF.primary }} />
                            <span>Safavieh International LLC</span>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="Broadway"
                        control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business sx={{ color: TOOL_COLORS.PDF.primary }} />
                            <span>Safavieh Broadway</span>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="Glen"
                        control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business sx={{ color: TOOL_COLORS.PDF.primary }} />
                            <span>Safavieh Glen Cove</span>
                          </Box>
                        }
                      />
                    </RadioGroup>
                    <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                      {getCompanyDescription()}
                    </Typography>
                  </FormControl>
                </CardContent>
              </Card>

              {/* File Upload */}
              <Card sx={{ mb: 3, bgcolor: 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Excel Import Files
                    </Typography>
                    {files.length > 0 && (
                      <Tooltip title={`${files.length} file(s) selected`}>
                        <Visibility sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Hidden File Input */}
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    multiple
                    onChange={handleFileUpload}
                    id="excel-file-input"
                    ref={fileInputRef}
                  />

                  {/* Drag & Drop Zone - Clickable */}
                  <Box
                    component="label"
                    htmlFor="excel-file-input"
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.PDF.primary : files.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragging
                        ? TOOL_COLORS.PDF.light
                        : files.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'block',
                      mb: 2,
                      '&:hover': {
                        borderColor: files.length > 0 ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                        bgcolor: files.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : TOOL_COLORS.PDF.light,
                      }
                    }}
                  >
                    {files.length > 0 ? (
                      <Box>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {files.length} file{files.length !== 1 ? 's' : ''} selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Drop more files or click to add
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.PDF.primary : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {isDragging ? 'Drop Excel files here' : 'Drag & drop Excel files here or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Supported formats: .xlsx, .xls (multiple files allowed)
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Selected Files List */}
                  {files.length > 0 && (
                    <Box sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
                      {files.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            mb: 1,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <FaFileExcel color="#10B981" />
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              ({formatFileSize(file.size)})
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => removeFile(index)}
                            disabled={processing}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Button
                    variant="outlined"
                    onClick={downloadTemplate}
                    startIcon={<GetApp sx={{ fontSize: '18px !important' }} />}
                    sx={{
                      borderColor: alpha(theme.palette.text.primary, 0.2),
                      color: alpha(theme.palette.text.primary, 0.7),
                      bgcolor: 'transparent',
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2.5,
                      py: 0.8,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: alpha(theme.palette.text.primary, 0.4),
                        color: theme.palette.text.primary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05),
                      },
                      '&:active': {
                        transform: 'scale(0.98)',
                      },
                    }}
                  >
                    Download Template
                  </Button>
                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={processInvoices}
                  disabled={files.length === 0 || processing}
                  startIcon={<FaFileInvoice />}
                  sx={{
                    bgcolor: TOOL_COLORS.PDF.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                    px: 4
                  }}
                >
                  {processing ? 'Generating Invoices...' : 'Generate Invoices'}
                </Button>

                {(files.length > 0 || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={processing}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Information Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Generating invoices for {companyType}...
                  </Typography>
                  <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.PDF.primary } }} />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.invoiceCount} Invoices Generated`}
                        secondary={`From ${results.processedCount} of ${results.totalCount} file(s)`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Business sx={{ color: TOOL_COLORS.PDF.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={results.companyType === 'LLC' ? 'Safavieh International LLC' : results.companyType === 'Broadway' ? 'Safavieh Broadway' : 'Safavieh Glen Cove'}
                        secondary="Entity format applied"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Receipt sx={{ color: TOOL_COLORS.PDF.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="PDF Format"
                        secondary="Ready for printing and distribution"
                      />
                    </ListItem>
                  </List>
                </Box>
              )}

              {/* Download Info */}
              {results && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Invoices have been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Expected File Format */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Required Excel Columns:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                {['Bill To', 'Invoice Date', 'Invoice No', 'Period', 'Description', 'Amount'].map((column, index) => (
                  <Chip
                    key={index}
                    label={column}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', borderColor: TOOL_COLORS.PDF.primary, color: theme.palette.text.primary }}
                  />
                ))}
              </Box>

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Select Safavieh entity (LLC/Broadway/Glen)<br />
                2. Download template or use your Excel file<br />
                3. Upload populated Excel file(s)<br />
                4. Generate expense allocation invoices<br />
                5. PDF files auto-download when complete<br /><br />
                <strong>Note:</strong> Multiple invoices can be generated from a single Excel file based on Invoice No.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SafaviehInvoices;
